import React from 'react';

/**
 * Fondo estilo pixel art para Data Adventure.
 * Cielo, nubes, montañas y pasto en bloques de 8px para look retro/aventura.
 */
const PIXEL_CLOUD = 12;
const cloud = '#FFFFFF';
const cloudShadow = '#E8F4FC';
const mountain1 = '#6B8E6B';
const mountain2 = '#5A7A5A';
const mountain3 = '#4A6A4A';
const grass = '#5A9A5A';
const grassDark = '#4A8A4A';
const path = '#C4A574';

// Forma de nube reconocible: ancha, redondeada (filas = y, columnas = x)
// 0 = vacío, 1 = pixel nube
const CLOUD_SHAPE = [
  [0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
  [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
  [0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0],
];

function CloudPixels({ top, left, right, center, color = cloud, shadowColor = cloudShadow }: {
  top: string;
  left?: string;
  right?: string;
  center?: boolean;
  color?: string;
  shadowColor?: string;
}) {
  const pixels: [number, number][] = [];
  CLOUD_SHAPE.forEach((row, y) =>
    row.forEach((cell, x) => {
      if (cell) pixels.push([x, y]);
    })
  );
  const w = CLOUD_SHAPE[0].length * PIXEL_CLOUD;
  const h = CLOUD_SHAPE.length * PIXEL_CLOUD;
  const style: React.CSSProperties = { top, width: w, height: h };
  if (center) {
    style.left = '50%';
    style.transform = 'translateX(-50%)';
  } else if (left !== undefined) style.left = left;
  else if (right !== undefined) style.right = right;

  return (
    <div className="absolute pointer-events-none" style={style}>
      {pixels.map(([x, y], i) => (
        <div
          key={i}
          className="absolute rounded-sm"
          style={{
            left: x * PIXEL_CLOUD,
            top: y * PIXEL_CLOUD,
            width: PIXEL_CLOUD,
            height: PIXEL_CLOUD,
            backgroundColor: y >= 2 ? shadowColor : color,
            boxShadow: '0 1px 0 rgba(255,255,255,0.6)',
          }}
        />
      ))}
    </div>
  );
}

export default function PixelArtAdventureBackground() {
  return (
    <div
      className="fixed inset-0 -z-10 overflow-hidden"
      aria-hidden
    >
      {/* Base: gradiente cielo */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(180deg, #87CEEB 0%, #B0E0E6 40%, #98D898 75%, #5A9A5A 100%)`,
        }}
      />

      {/* Capa pixel art: montañas lejanas (SVG escalado) */}
      <svg
        className="absolute bottom-0 left-0 w-full h-2/3 min-h-[400px] opacity-90"
        viewBox="0 0 400 200"
        preserveAspectRatio="none"
      >
        <polygon points="0,200 0,120 80,160 160,80 240,140 320,60 400,120 400,200" fill={mountain3} />
        <polygon points="0,200 0,140 60,180 140,100 220,160 300,90 400,150 400,200" fill={mountain2} />
        <polygon points="0,200 0,160 100,200 200,120 300,180 400,160 400,200" fill={mountain1} />
      </svg>

      {/* Nubes con forma reconocible (redondeadas, anchas) */}
      <CloudPixels top="8%" left="5%" />
      <CloudPixels top="18%" right="8%" />
      <CloudPixels top="4%" center />

      {/* Pasto/piso pixelado (franja inferior) */}
      <div
        className="absolute bottom-0 left-0 right-0 h-24 opacity-95"
        style={{
          backgroundImage: `
            repeating-linear-gradient(
              90deg,
              ${grass} 0px,
              ${grass} 8px,
              ${grassDark} 8px,
              ${grassDark} 16px
            ),
            repeating-linear-gradient(
              0deg,
              ${grass} 0px,
              ${grass} 8px,
              ${grassDark} 8px,
              ${grassDark} 16px
            )
          `,
          backgroundSize: '16px 16px',
          imageRendering: 'pixelated',
        }}
      />

      {/* Sendero/camino pixelado (opcional, centrado abajo) */}
      <div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 w-32 h-6 rounded-sm opacity-80"
        style={{
          backgroundColor: path,
          boxShadow: 'inset 0 0 0 2px #A08050',
          imageRendering: 'pixelated',
        }}
      />
    </div>
  );
}
